/**
 * ConstanciaCard
 *
 * Caja informativa con acceso al PDF de la Constancia Cesion Parcial
 * asociada a un CreditContract. Va debajo de los paneles CC <-> CR en el
 * CollectorDetailModal.
 *
 * Estados:
 *  - No aplica: si el row no trae `constanciaS3Key`, no se renderiza
 *    (CC LEGACY o caso donde la generacion del PDF fallo).
 *  - Disponible: caja teal con hash, fecha, y boton "Ver Constancia (PDF)".
 *  - Error post-click: caja teal + mensaje rojo del backend.
 *
 * El presigned URL lo entrega credit-engine via /backoffice/contracts/pdf-by-key
 * usando directamente el S3 key (no depende de ContractControl).
 */

import { useState } from 'react';
import { FileText, AlertCircle, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useConstanciaUrl } from '@/features/reconciliation/hooks/useConstanciaUrl';
import { ConstanciaViewerModal } from './ConstanciaViewerModal';

interface Props {
  ccId: string;
  constanciaS3Key?: string | null;
  constanciaHash?: string | null;
  ccCreateDate?: string;
}

function formatDateShort(iso?: string): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function truncateHash(hash?: string | null): string {
  if (!hash) return '-';
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

export function ConstanciaCard({ ccId, constanciaS3Key, constanciaHash, ccCreateDate }: Props) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const { fetchUrl, url, isLoading, isError, error, reset } = useConstanciaUrl(constanciaS3Key ?? null);

  if (!constanciaS3Key) {
    return null;
  }

  const handleClickView = () => {
    setViewerOpen(true);
    if (!url) fetchUrl();
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    reset();
  };

  const errorMessage = isError ? (error?.message ?? 'Error desconocido al solicitar el PDF') : null;

  return (
    <>
      <div className="rounded-lg border border-teal-500/30 bg-teal-500/5 p-3">
        <div className="flex items-start gap-3">
          <FileText size={16} className="mt-0.5 text-teal-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-foreground">Constancia Cesión Parcial</p>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                Hash:{' '}
                <span className="font-mono text-foreground" title={constanciaHash ?? ''}>
                  {truncateHash(constanciaHash)}
                </span>
              </span>
              <span>
                Generada:{' '}
                <span className="text-foreground">{formatDateShort(ccCreateDate)}</span>
              </span>
            </div>
            {isError && (
              <div className="mt-2 flex items-start gap-1.5 text-xs text-red-400">
                <AlertCircle size={12} className="mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={isLoading}
            onClick={handleClickView}
          >
            {isLoading ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Cargando…
              </>
            ) : (
              <>
                <Eye size={14} />
                Ver Constancia (PDF)
              </>
            )}
          </Button>
        </div>
      </div>

      <ConstanciaViewerModal
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
        ccId={ccId}
        url={url}
        isLoading={isLoading}
        errorMessage={errorMessage}
      />
    </>
  );
}
